import SubmissionService from '@/core/collections/submission/submission.service';
import { getLocalizedName } from '@/core/helpers/translationHelper';
import router from '@/core/router';
import AnswerService from '@/core/services/answer';
import candidateStepService from '@/core/services/candidateStep';
import candidatefieldService from '@/core/services/candidatefield';
import ChoiceService from '@/core/services/choice';
import ColorSchemeService from '@/core/services/colorScheme';
import ConsultantService from '@/core/services/consultant';
import CustomerService from '@/core/services/customer';
import FileService from '@/core/services/file';
import JourneyService from '@/core/services/journey';
import JourneyLanguageService from '@/core/services/journeyLanguage';
import JourneyStateService from '@/core/services/journeyState';
import JourneyTypeService from '@/core/services/journeyType';
import LanguageService from '@/core/services/language';
import ModuleService from '@/core/services/module';
import moduleAttributeAppearanceService from '@/core/services/moduleAttributeAppearance';
import PageTypeService from '@/core/services/pageType';
import PolicyService from '@/core/services/policy';
import PoolService from '@/core/services/pool';
import PropertyService from '@/core/services/property';
import ReasonService from '@/core/services/reason';
import ReferralService from '@/core/services/referral';
import ReferralTypeService from '@/core/services/referralType';
import RoleService from '@/core/services/role';
import StepService from '@/core/services/step';
import StepRequirementService from '@/core/services/stepRequirement';
import StepTemplateService from '@/core/services/stepTemplate';
import TagService from '@/core/services/tag';
import TenantJourneyService from '@/core/services/tenantJourney';
import TextService from '@/core/services/text';
import fileTypesEnum from '@/core/enums/fileTypes';
import i18n from '@/i18n';
import { NotificationType } from '@nxtpeople/nxt-styles';
import { LocationQuery } from 'vue-router';
import { Module } from 'vuex';
//#region Services
const answerService = new AnswerService();
const submissionService = new SubmissionService();
const choiceService = new ChoiceService();
const colorSchemeService = new ColorSchemeService();
const consultantService = new ConsultantService();
const customerService = new CustomerService();
const fileService = new FileService();
const journeyService = new JourneyService();
const journeyStateService = new JourneyStateService();
const journeyTypeService = new JourneyTypeService();
const languageService = new LanguageService();
const moduleService = new ModuleService();
const textService = new TextService();
const journeyLanguageService = new JourneyLanguageService();
const referralService = new ReferralService();
const referralTypeService = new ReferralTypeService();
const stepService = new StepService();
const stepTemplateService = new StepTemplateService();
const tagService = new TagService();
const pageTypeService = new PageTypeService();
const policyService = new PolicyService();
const propertyService = new PropertyService();
const poolService = new PoolService();
const roleService = new RoleService();
const stepRequirementService = new StepRequirementService();
const tenantJourneyService = new TenantJourneyService();
const reasonService = new ReasonService();
//#endregion Service

async function refreshJourneyAndCurrentStep(context: any, journeyId: any) {
   const refreshedJourney = await context.dispatch('getSetJourney', { id: journeyId });
   const currentStepId = context.state.step?.id;
   if (!currentStepId || !refreshedJourney?.steps) return refreshedJourney;

   const matchingStep = refreshedJourney.steps.find((step: any) => step.id === currentStepId);
   if (matchingStep) {
      context.commit('setStep', matchingStep);
   }

   return refreshedJourney;
}

const journeysStore: Module<any, any> = {
   state: {
      // consultant: null,
      // modules: [],
      activeTab: 'dashboard',
      canFocusChange: true,
      candidatefields: [],
      changedInput: [],
      changedJourneySettings: [],
      changedStepSettings: [],
      duplicatedModule: null,
      isJourneySettingsShown: false,
      isSaving: false,
      journey: null,
      journeyLanguage: null,
      journeyStates: [],
      journeyTypes: [],
      journeys: [],
      languages: [],
      languagesToAdd: [],
      languagesToRemove: [],
      pageTypes: [],
      properties: [],
      reasons: [],
      referralTypes: null,
      referrals: null,
      step: null,
      notificationTypes: [],
   },

   getters: {
      // modules: (state) => {
      //     return state.modules;
      // },
      submissionStates: (state) => {
         return state.submissionStates;
      },
      referrals: (state) => {
         return state.referrals;
      },
      referralTypes: (state) => {
         return state.referralTypes;
      },
      step: (state) => {
         return state.step;
      },
      journey: (state) => {
         return state.journey;
      },
      journeys: (state) => {
         return state.journeys;
      },
      isSaving: (state) => {
         return state.isSaving;
      },
      notificationTypes: (state) => {
         return state.notificationTypes;
      },

      isJourneySettingsShown: (state) => {
         return state.isJourneySettingsShown;
      },
      changedJourneySettings: (state) => {
         return state.changedJourneySettings;
      },
      changedStepSettings: (state) => {
         return state.changedStepSettings;
      },
      duplicatedModule: (state) => {
         return state.duplicatedModule;
      },
      canFocusChange: (state) => {
         return state.canFocusChange;
      },
      activeTab: (state) => {
         return state.activeTab;
      },
      languages: (state) => {
         return state.languages;
      },
      journeyLanguage: (state) => {
         return state.journeyLanguage;
      },
      languagesToAdd: (state) => {
         return state.languagesToAdd;
      },
      languagesToRemove: (state) => {
         return state.languagesToRemove;
      },
      changedInput: (state) => {
         return state.changedInput;
      },
      propertyNameById: (state) => (propertyId: Number) => {
         const property = state.properties.find((property: any) => property.id == propertyId);
         return (
            getLocalizedName(property?.name, state.journeyLanguage.code) ||
            getLocalizedName(property?.texts?.[0]?.content, state.journeyLanguage.code)
         );
      },
      pageTypes: (state) => {
         return state.pageTypes;
      },
      journeyStates: (state) => {
         return state.journeyStates;
      },

      orderedReasons: (state) => {
         return state.reasons
            .filter((reason: any) => reason.key !== 'can-continue')
            .sort((a: any, b: any) => {
               if (a.key == 'others') return 1;
               if (a.texts[0].content['nl'] < b.texts[0].content['nl']) return -1;
               if (a.texts[0].content['nl'] > b.texts[0].content['nl']) return 1;
               return 0;
            });
      },
      // consultant: (state) => {
      //    return state.consultant;
      // },
      languageById: (state) => (language_id: Number) => {
         return state.languages.find((language: any) => language.id == language_id);
      },
   },

   mutations: {
      setReferrals(state, newValue) {
         state.referrals = newValue;
      },
      setReferralTypes(state, newValue) {
         state.referralTypes = newValue;
      },

      setIsSaving(state, newValue) {
         state.isSaving = newValue;
      },

      deleteModule(state, module) {
         state.step.modules = state.step.modules.filter((currModule: any) => currModule.id != module.id);
         state.journey.steps.find((step: any) => step.id == module.step_id).modules = state.step.modules;
         const modulesCurrentPage = state.step.modules.filter((currModule: any) => currModule.page_sequence == module.page_sequence);
         if (modulesCurrentPage.length == 0) {
            state.step.modules
               .filter((currModule: any) => currModule.page_sequence > module.page_sequence)
               .forEach((currModule: any) => {
                  currModule.page_sequence -= 10;
               });
         }
      },

      updateModule(state, module) {
         var foundIndex = state.step.modules.findIndex((m: any) => m.id == module.id);
         state.step.modules[foundIndex] = module;
      },

      setModuleDirty(state, module) {
         module.is_dirty = true;
         this.commit('updateModule', module);
      },
      SET_NOTIFICATION_TYPES(state, notificationTypes) {
         state.notificationTypes = notificationTypes;
      },

      setSubmissionStates(state, value) {
         state.submissionStates = value;
      },
      setIsJourneySettingsShown(state, value) {
         state.isJourneySettingsShown = value;
      },
      setJourney(state, journey) {
         state.journey = journey;
      },
      setJourneys(state, journeys) {
         state.journeys = journeys;
      },

      setStep(state, step) {
         state.step = step;
      },

      deleteStep(state, { stepId, stepSequence }) {
         state.journey.steps = state.journey.steps.filter((step: any) => {
            return step.id != stepId;
         });
         // enkel dit doen voor de journeys die na de verwijderde journey kwamen

         state.journey.steps
            .filter((step: any) => step.sequence > stepSequence)
            .forEach((step: any) => {
               step.sequence = step.sequence - 10;
            });

         if (state.journey.steps.length != 0) {
            state.step = state.journey.steps[0];
            router.push({
               name: 'journey-edit-page',
               params: {
                  journeyId: state.journey.id,
                  step: '1',
                  page: '1',
               },
            });
         } else {
            state.step = {};
            router.push({
               name: 'journey-edit',
               params: {
                  journeyId: state.journey.id,
               },
            });
         }
      },

      addStepToJourney(state, step) {
         state.journey.steps.push(step);
      },

      updateStepToJourney(state, step) {
         var foundIndex = state.journey.steps.findIndex((x: any) => x.id == step.id);
         state.journey.steps[foundIndex] = step;
      },

      setLanguages(state, languages) {
         state.languages = languages;
         state.language = state.languages[0];
      },
      setJourneyLanguage(state, language) {
         state.journeyLanguage = language;
      },

      clearChangedJourneySettings(state) {
         state.changedJourneySettings = [];
      },
      clearChangedStepSettings(state) {
         state.changedStepSettings = [];
      },

      addChangedSettings(state, { changedSetting, setting }) {
         const foundChangedSetting = state[setting].find((setting: any) => setting.path == changedSetting.path);
         if (foundChangedSetting) {
            state[setting][state[setting].indexOf(foundChangedSetting)] = changedSetting;
         } else {
            state[setting].push(changedSetting);
         }
      },
      setLanguagesToAdd(state, languagesToAdd) {
         state.languagesToAdd = languagesToAdd;
      },
      setLanguagesToRemove(state, languagesToRemove) {
         state.languagesToRemove = languagesToRemove;
      },
      setDuplicatedModule(state, duplicatedModule) {
         state.duplicatedModule = duplicatedModule;
      },
      setCanFocusChange(state, canFocusChange) {
         state.canFocusChange = canFocusChange;
      },
      setActiveTab(state, activeTab) {
         state.activeTab = activeTab;
      },
      setChangedInput(state, changedInput) {
         state.changedInput = changedInput;
      },
      setProperties(state, properties) {
         state.properties = properties;
      },
      addProperty(state, property) {
         state.properties.push(property);
      },
      replaceProperty(state, property) {
         const index = state.properties.findIndex((p: any) => p.id === property.id);
         if (index !== -1) {
            state.properties[index] = property;
         }
      },
      removeProperty(state, id) {
         state.properties = state.properties.filter((p: any) => p.id !== id);
      },
      setPageTypes: (state, pageTypes) => {
         return (state.pageTypes = pageTypes);
      },
      setJourneyStates(state, journeyStates) {
         state.journeyStates = journeyStates;
      },
      setJourneyTypes(state, journeyTypes) {
         state.journeyTypes = journeyTypes;
      },
      setJourneyParameter(state, { path, value }) {
         if (!(typeof value == 'object')) {
            state.journey[path] = value;
         } else {
            state.journey[path] = { ...state.journey[path], [Object.keys(value)[0]]: Object.values(value)[0] };
         }
      },
      setReason(state, reasons) {
         state.reasons = reasons;
      },
      setConsultant(state, consultant) {
         state.consultant = consultant;
      },
   },

   actions: {
      //#region Answer
      async getAnswer(context, { id }) {
         return await answerService.getAnswer(id).then((response: any) => {
            return response;
         });
      },

      async createAnswer(context, { body }) {
         return await answerService
            .createAnswer(body)
            .then((response: any) => {
               return response;
            })
            .catch((error) => {
               throw error;
            });
      },

      async bulkCreateAnswers(context, { body }) {
         return await answerService.bulkCreateAnswers(body).then((response: any) => {
            return response;
         });
      },

      async editAnswer(context, { id, body, submission_random }) {
         return await answerService
            .editAnswer(id, body, submission_random)
            .then((response: any) => {
               return response;
            })
            .catch((error) => {
               throw error;
            });
      },

      async deleteAnswer(context, { id, submission_random }) {
         return await answerService
            .deleteAnswer(id, submission_random)
            .then((response: any) => {
               return response;
            })
            .catch((error) => {
               throw error;
            });
      },

      //#endregion

      //#region Candidatefield
      async getCandidatefields(context) {
         return await candidatefieldService.get().then((response: any) => {
            return response;
         });
      },
      //#endregion CandidateField

      //#region CandidateStep
      async patchCandidateStep(context, { patches, candidate_step_id }) {
         var body: Object[] = [];
         patches.forEach((patch: Object) => {
            body.push({
               op: 'replace',
               path: Object.keys(patch)[0],
               value: Object.values(patch)[0],
            });
         });

         if (body.length != 0)
            return await candidateStepService.patch(body, candidate_step_id).then((response: any) => {
               return response;
            });
      },
      //#endregion CandidateStep

      //#region Choice
      async createChoice(context, { moduleId, correct = null }) {
         context.commit('setIsSaving', true);

         return await choiceService
            .createChoice({
               module_id: moduleId,
               correct: correct,
            })
            .then((response) => {
               context.commit('setIsSaving', false);
               return response;
            });
      },
      async getChoice(context, choiceId) {
         return await choiceService
            .getChoice(choiceId)
            .then((response) => {
               return response;
            })
            .catch((error) => {
               throw error;
            });
      },
      async updateChoice(context, { choiceId, moduleId, content, correct, tag = null, tagId = null, sequence, fileId }) {
         context.commit('setIsSaving', true);

         // if (correct === null) {
         //     correct = false
         // }

         var body: any = {
            module_id: moduleId,
            content: content,
            correct: correct,
            sequence: sequence,
         };
         if (tag != null) {
            body.tag = tag;
         }
         if (tagId != null) {
            body.tag_id = tagId;
         }
         if (fileId != null) {
            body.file_id = fileId;
         }
         return await choiceService.updateChoice(choiceId, body).then((response) => {
            context.commit('setIsSaving', false);
         });
      },
      async patchChoice(context, { choiceId, path, value, op = 'replace' }) {
         context.commit('setIsSaving', true);
         var body = [
            {
               op,
               path,
               value,
            },
         ];
         return await choiceService
            .patchChoice(choiceId, body)
            .then((response) => {
               context.commit('setIsSaving', false);
               return response;
            })
            .catch((error) => {
               context.commit('setIsSaving', false);

               throw error;
            });
      },
      async deleteChoice(context, { choiceId }) {
         context.commit('setIsSaving', true);
         return await choiceService.deleteChoice(choiceId).then((response) => {
            context.commit('setIsSaving', false);
         });
      },
      async getChoiceAnswersCount(context, choiceId) {
         return await choiceService.getAnswersCount(choiceId);
      },
      //#endregion Choice

      //#region ColorScheme
      async getColorSchemes() {
         return await colorSchemeService.getColorSchemes().then((response) => {
            return response;
         });
      },
      //#endregion ColorScheme

      //#region Consultant
      async getConsultants(context, cancelToken) {
         return await consultantService.getConsultants(cancelToken).then((response) => {
            return response;
         });
      },
      //#endregion

      //#region Customer
      async getCustomers(context, { query }: { query: LocationQuery }) {
         return await customerService.getCustomers(query).then((response) => {
            return response;
         });
      },
      //#endregion Customer

      //#region Export
      async getJourneyExport(context, { journeyId }) {
         return await journeyService.getExport(journeyId, context.getters.journeyLanguage.code).then((journey) => {
            context.dispatch('addNotification', { notification: { type: 'success', title: i18n.global.t('export_sent_to_mail') } });

            return journey;
         });
      },
      async getStatusExport({ getters }, { journeyId }) {
         return await journeyService.getStatusExport(journeyId, getters.startDateStatus, getters.endDateStatus).then((journey) => {
            return journey;
         });
      },
      async getReferralSubmissionStateExport(context, { journeyId }) {
         return await journeyService.getReferralSubmissionStateExport(journeyId).then((journey) => {
            return journey;
         });
      },
      //#endregion Export

      //#region File
      async createFile(context, { file, fileOf, fileOfId, language_id }) {
         context.commit('setIsSaving', true);

         const mimeType: string = file.type ?? '';
         let type: string = fileTypesEnum.document;
         if (mimeType.startsWith('image/')) type = fileTypesEnum.image;
         else if (mimeType.startsWith('video/')) type = fileTypesEnum.video;
         else if (mimeType === 'application/pdf') type = fileTypesEnum.pdf;
         else if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) type = fileTypesEnum.powerpoint;

         var formData = new FormData();
         formData.append('model_type', fileOf);
         formData.append('model_id', fileOfId);
         formData.append('file_of', fileOf);
         formData.append('file_of_id', fileOfId);
         formData.append('type', type);
         formData.append('media', file);
         if (language_id) formData.append('language_id', language_id);

         return await fileService
            .createFile(formData)
            .then((response) => {
               context.commit('setIsSaving', false);
               return response;
            })
            .catch((error) => {
               context.commit('setIsSaving', false);

               throw error;
            });
      },
      async getFile(context, { fileId }) {
         return await fileService.getFile(fileId).then((response) => {
            return response;
         });
      },
      async deleteFile(context, { fileId }) {
         context.commit('setIsSaving', true);
         return await fileService
            .deleteFile(fileId)
            .then((response) => {
               context.commit('setIsSaving', false);
            })
            .catch((error) => {
               context.commit('setIsSaving', false);

               throw error;
            });
      },
      //#endregion File

      //#region Journey
      async getSetJourneys(context, { search, sort, types = [], states = [], page, per_page, trashed, validatable }) {
         const response = await journeyService.getJourneys(search, sort, types, states, page, per_page, trashed, validatable);
         context.commit('setJourneys', response.data);
         context.commit('setJourney', null);
         return response;
      },
      async getSetJourney(context, { id }) {
         return await journeyService.getJourney(id).then((response) => {
            context.commit('setJourney', response);
            if (response.languages.length != 0 && !context.getters.journeyLanguage) {
               context.commit('setJourneyLanguage', Object.values(response.languages)[0]);
            }
            return response;
         });
      },
      async getJourney(context, { id }) {
         return await journeyService.getJourney(id).then((response) => {
            return response;
         });
      },
      async createJourney(context, { body: body }) {
         return await journeyService.createJourney(body).then((response) => {
            return response;
         });
      },
      async patchJourney(context, { id, path, value, op = 'replace' }) {
         let body = [
            {
               op: op,
               path: path,
               value: value,
            },
         ];
         context.commit('setIsSaving', true);

         return await journeyService
            .patchJourney(id, body)
            .then((response) => {
               context.commit('setIsSaving', false);
               return response;
            })
            .catch((error) => {
               context.commit('setIsSaving', false);

               throw error;
            });
      },

      async putJourney(context, { id: id, body: body }) {
         context.commit('setIsSaving', true);
         return await journeyService
            .putJourney(id, body)
            .then((response) => {
               context.commit('setIsSaving', false);
               return response;
            })
            .catch((error) => {
               context.commit('setIsSaving', false);

               throw error;
            });
      },

      async requestJourneyApproval(context, { id }) {
         const response = await journeyService.requestJourneyApproval(id);
         if (response?.message) {
            context.dispatch('addNotification', { notification: { type: 'success', title: response.message } }, { root: true });
         }
         await context.dispatch('getSetJourney', { id });
         return response;
      },

      async approveJourney(context, { id }) {
         const response = await journeyService.approveJourney(id);
         await context.dispatch('getSetJourney', { id });
         return response;
      },

      async rejectJourney(context, { id, feedback }) {
         const response = await journeyService.rejectJourney(id, feedback);
         await context.dispatch('getSetJourney', { id });
         return response;
      },

      async cancelJourneyReview(context, { id }) {
         const response = await journeyService.cancelJourneyReview(id);
         await context.dispatch('getSetJourney', { id });
         return response;
      },

      async setJourneyDraft(context, { id }) {
         const response = await journeyService.setJourneyDraft(id);
         await context.dispatch('getSetJourney', { id });
         return response;
      },

      async deleteJourney(context, { journeyId }) {
         return await journeyService.deleteJourney(journeyId).then((response) => {
            context.commit('setIsSaving', false);
            return response;
         });
      },

      async restoreJourney(context, { journeyId }) {
         context.commit('setIsSaving', true);
         return await journeyService.restoreJourney(journeyId).then((response) => {
            context.commit('setIsSaving', false);
            context.state.journey.deleted_at = null;
            return response;
         });
      },

      async duplicateJourney(context, { journeyId, locale }) {
         let infoToast: any = {};

         var message = i18n.global.t('journey_duplicating', locale);
         context
            .dispatch(
               'addNotification',
               {
                  notification: {
                     type: 'info',
                     title: message,
                     duration: 1000000,
                  },
               },
               { root: true }
            )
            .then((response) => {
               infoToast = response;
            });

         return await journeyService
            .duplicateJourney(journeyId)
            .then((response) => {
               context.dispatch('removeNotification', { notification_key: infoToast.key }, { root: true });

               var message = i18n.global.t('journey_duplicated', { journeyId: response.id }, locale);

               context
                  .dispatch(
                     'addNotification',
                     {
                        notification: {
                           type: 'success',
                           title: message,
                           duration: 120000,
                        },
                     },
                     { root: true }
                  )
                  .then((response) => {
                     return response;
                  });
               return response;
            })
            .catch((error) => {
               context.dispatch('removeNotification', { notification_key: infoToast.key }, { root: true });
               context
                  .dispatch(
                     'addNotification',
                     {
                        notification: {
                           type: NotificationType.Danger,
                           title: error.message,
                        },
                     },
                     { root: true }
                  )
                  .then((response) => {
                     return response;
                  });
               throw error;
            });
      },
      async getJourneyAnomalies(context, { journeyId, cancelToken }) {
         return await journeyService.getJourneyAnomalies(journeyId, cancelToken).then((response) => {
            return response;
         });
      },
      async getJourneyResult(context, { journeyId }) {
         return await journeyService.showResult(journeyId);
      },
      async getJourneyHistory(context, { journeyId, query }) {
         return await journeyService.getJourneyHistory(journeyId, query).then((response) => {
            return response;
         });
      },
      //#endregion Journey

      //#region JourneyLanguage
      async createJourneyLanguage(context, { journeyId, languageId }) {
         context.commit('setIsSaving', true);
         var body = {
            journey_id: journeyId,
            language_id: languageId,
         };

         return await journeyLanguageService.postJourneyLanguage(body).then((response: any) => {
            context.commit('setIsSaving', false);
            return response;
         });
      },
      async postJourneyLanguage(context, { journeyId, languageId }) {
         const body = {
            journey_id: journeyId,
            language_id: languageId,
         };
         return await journeyService
            .postJourneyLanguage(body)
            .then(async (response) => {
               await refreshJourneyAndCurrentStep(context, journeyId);
               return response;
            })
            .catch((error) => {
               context
                  .dispatch(
                     'addNotification',
                     {
                        notification: {
                           type: NotificationType.Danger,
                           title: error.message,
                        },
                     },
                     { root: true }
                  )
                  .then((response) => {
                     return response;
                  });
               throw error;
            });
      },
      async deleteJourneyLanguage(context, { id }) {
         const journeyId = context.getters.journey?.id;
         return await journeyService
            .deleteJourneyLanguage(id)
            .then(async (response) => {
               if (journeyId) {
                  await refreshJourneyAndCurrentStep(context, journeyId);
               }
               return response;
            })
            .catch((error) => {
               context
                  .dispatch(
                     'addNotification',
                     {
                        notification: {
                           type: NotificationType.Danger,
                           title: error.message,
                        },
                     },
                     { root: true }
                  )
                  .then((response) => {
                     return response;
                  });
               throw error;
            });
      },
      //#endregion JourneyLanguage

      //#region JourneyState
      async getJourneyStates(context) {
         return await journeyStateService.getJourneyStates().then((response) => {
            context.commit('setJourneyStates', response);
            return response;
         });
      },
      //#endregion JourneyState

      //#region JourneyType
      async getJourneyTypes(context) {
         return await journeyTypeService.getJourneyTypes().then((response) => {
            context.commit('setJourneyTypes', response);
            return response;
         });
      },
      //#endregion JourneyType

      //#region Language
      async setLanguages(context) {
         return await languageService.getLanguages().then((response) => {
            context.commit('setLanguages', response.data);
            return response.data;
         });
      },

      //#endregion Language

      //#region Module
      async createModule(context, { stepId, moduleTypeId, title, pageSequence, sequence }) {
         context.commit('setIsSaving', true);
         var body = {
            step_id: stepId,
            module_type_id: moduleTypeId,
            title: title,
            page_sequence: pageSequence,
            sequence: sequence,
         };
         return await moduleService
            .createModule(body)
            .then((response) => {
               context.commit('setIsSaving', false);
               return response;
            })
            .catch((error) => {
               context.commit('setIsSaving', false);

               throw error;
            });
      },
      async getModule(context, moduleId) {
         context.commit('setIsSaving', true);
         return await moduleService
            .getModule(moduleId)
            .then((response) => {
               context.commit('setIsSaving', false);
               return response;
            })
            .catch((error) => {
               context.commit('setIsSaving', false);

               throw error;
            });
      },
      async getPossibleModuleAttributes(context, moduleId) {
         context.commit('setIsSaving', true);
         return await moduleService
            .getPossibleModuleAttributes(moduleId)
            .then((response) => {
               context.commit('setIsSaving', false);
               return response;
            })
            .catch((error) => {
               context.commit('setIsSaving', false);

               throw error;
            });
      },
      async updateModule(context, { body: body }) {
         context.commit('setIsSaving', true);
         return await moduleService
            .updateModule(body)
            .then((response) => {
               context.commit('setIsSaving', false);
               return response;
            })
            .catch((error) => {
               context.commit('setIsSaving', false);

               throw error;
            });
      },
      async patchModule(context, { moduleId, path, value }) {
         context.commit('setIsSaving', true);
         var body = [
            {
               op: 'replace',
               path: path,
               value: value,
            },
         ];
         return await moduleService
            .patchModule(moduleId, body)
            .then((response) => {
               context.commit('setIsSaving', false);
               return response;
            })
            .catch((error) => {
               context.commit('setIsSaving', false);

               throw error;
            });
      },
      async deleteModule(context, { module }) {
         context.commit('setIsSaving', true);
         return await moduleService.deleteModule(module.id).then(() => {
            context.commit('deleteModule', module);
            context.commit('setIsSaving', false);
         });
      },
      async resetModule(context, { module }) {
         context.commit('setIsSaving', true);
         return await moduleService.resetModule(module.id).then((response) => {
            context.commit('updateModule', response);
            context.commit('setIsSaving', false);
            return response;
         });
      },
      async duplicateModule(context, { module }) {
         context.commit('setIsSaving', true);

         return await moduleService.duplicateModule(module.id).then((response) => {
            context.commit('setIsSaving', false);
            return response;
         });
      },
      async copyModuleAttributes(context, { moduleId, originLanguageId, targetLanguageId }) {
         return await moduleService.copyModuleAttributes(moduleId, originLanguageId, targetLanguageId).then((response) => {
            context.commit('setIsSaving', false);
            return response;
         });
      },
      //#endregion Module

      //#region ModuleAttributeAppearance
      async postModuleAttributeAppearance(context, body) {
         return await moduleAttributeAppearanceService.post(body).then((response: any) => {
            return response;
         });
      },
      async deleteModuleAttributeAppearance(context, module_attribute_appearance_id) {
         return await moduleAttributeAppearanceService.delete(module_attribute_appearance_id);
      },
      async patchModuleAttributeAppearance(context, { module_attribute_appearance_id, patches }) {
         return await moduleAttributeAppearanceService.patch(module_attribute_appearance_id, patches);
      },
      //#endregion ModuleAttributeAppearance

      //#region Notifications
      async setNotificationTypes(context) {
         return await journeyService.getNotificationTypes().then((response) => {
            context.commit('SET_NOTIFICATION_TYPES', response);
            return response;
         });
      },
      //#endregion Notifications

      //#region PageType
      async getPageTypes(context) {
         return await pageTypeService.getPageTypes().then((response) => {
            context.commit('setPageTypes', response);
            return response;
         });
      },
      //#endregion PageType

      //#region Permission
      async getPermissionRoles(context) {
         return await roleService.getPermissionRoles().then((response) => {
            return response;
         });
      },
      async postPermissionRole(context, { roleId, permissionId }) {
         return await roleService.postPermissionRole(roleId, permissionId).then((response) => {
            return response;
         });
      },
      async deletePermissionRole(context, permissionRoleId) {
         return await roleService.deletePermissionRole(permissionRoleId).then((response) => {
            return response;
         });
      },
      //#endregion Permission

      //#region Policy
      async getPolicies(context) {
         return await policyService.getPolicies().then((response) => {
            return response;
         });
      },
      async postPolicyable(context, { policyId, policyableId, policyableType, allowed }) {
         return await policyService.postPolicyable(policyId, policyableId, policyableType, allowed).then((response) => {
            return response;
         });
      },
      async patchPolicyable(context, { policyableId, allowed, path }) {
         return await policyService.patchPolicyable(policyableId, allowed, path).then((response) => {
            return response;
         });
      },
      async deletePolicyable(context, policyableId) {
         return await policyService.deletePolicyable(policyableId).then((response) => {
            return response;
         });
      },
      //#endregion Policy

      //#region Pool
      async getNxtPlanPools({}, { search }) {
         return await poolService.get(search).then((response) => response.data);
      },
      //#endregion Pool

      //#region Property
      async getProperties(context, journeyId) {
         return await propertyService.getProperties(journeyId).then((response) => {
            context.commit('setProperties', response);
         });
      },
      async createProperty(context, { journeyId, name, moduleTypeIds }) {
         return await propertyService.createProperty({ name, journey_id: journeyId, module_type_ids: moduleTypeIds }).then((response) => {
            context.commit('addProperty', response);
            return response;
         });
      },
      async updateProperty(context, { id, name }) {
         return await propertyService.updateProperty(id, { name }).then((response) => {
            context.commit('replaceProperty', response);
            return response;
         });
      },
      async deleteProperty(context, id) {
         return await propertyService.deleteProperty(id).then((response) => {
            context.commit('removeProperty', id);
            return response;
         });
      },
      //#endregion Property

      //#region Reason
      async getReasons(context) {
         return await reasonService.get().then((response) => {
            context.commit('setReason', response);
            return response;
         });
      },
      //#endregion Reason

      //#region Referral
      async getReferrals(context) {
         return await referralService.getReferrals().then((response) => {
            context.commit('setReferrals', response);
            return response;
         });
      },
      async createReferral(context, { code, colorSchemeId, journeyTeamId, languageId, referralTypeId, nxt_plan_pool_id = null }) {
         var body = {
            code: code,
            journey_team_id: journeyTeamId,
            language_id: languageId,
            referral_type_id: referralTypeId,
            nxt_plan_pool_id,
            shareable: 1,
         };
         return await referralService
            .createReferral(body)
            .then((response) => {
               return response;
            })
            .catch((error) => {
               throw error;
            });
      },
      async getAnswersPerReferralExport({ getters }, { journeyId }) {
         return await referralService.getAnswersPerReferralExport(journeyId, getters.startDate, getters.endDate).then((journey) => {
            return journey;
         });
      },
      async getReferralExport({ getters }, { journeyId }) {
         return await referralService.getExport(journeyId, getters.startDate, getters.endDate).then((journey) => {
            return journey;
         });
      },
      async getJourneyReferrals(context, { journeyId, cancelToken }) {
         return await referralService.getJourneyReferrals(journeyId, cancelToken).then((response) => {
            return response;
         });
      },
      async patchReferral(context, { id, path, value }) {
         const BODY = [
            {
               op: 'replace',
               path: path,
               value: value,
            },
         ];

         return await referralService.patch(id, BODY);
      },
      //#endregion Referral

      //#region ReferralType
      async getReferralTypes({ commit, state }) {
         if (!state.referralTypes == null) return;
         await referralTypeService.get().then((response) => commit('setReferralTypes', response));
      },
      //#endregion ReferralType

      //#region Role
      async getRoles(context) {
         return await roleService.getRoles().then((response) => {
            return response;
         });
      },
      //#endregion Role

      //#region Setting
      async changeSettings(context, { journeyStep, journeyId, journeyStepId }) {
         context.commit('setIsSaving', true);
         let journeySettings = journeyStep == 'journey' ? context.state.changedJourneySettings : context.state.changedStepSettings;
         return await journeyService.changeSettings(journeyStep, journeyStepId, journeySettings).then((response) => {
            this.dispatch('getSetJourney', { id: journeyId }).then((response) => {
               let newStep = journeyStep != 'journey' ? response.steps.find((step: any) => step.id == context.state.step.id) : null;
               if (newStep) {
                  this.commit('setStep', newStep);
               }
            });
            context.commit('clearChangedJourneySettings');
            context.commit('clearChangedStepSettings');
            context.commit('setIsSaving', false);
            return response;
         });
      },
      //#endregion Setting

      //#region Step
      async createStep(context, { journeyId, step_template_id, stepTitle }) {
         //  'journey_id' => 'numeric|min:1|required'
         // 'pure_template' => 'boolean|required',
         // 'name' => '',
         context.commit('setIsSaving', true);
         return await stepService
            .createStep({
               journey_id: journeyId,
               step_template_id: step_template_id,
               name: stepTitle,
            })
            .then((response) => {
               context.commit('addStepToJourney', response);
               context.commit('setIsSaving', false);
               return response;
            });
      },
      async getStep(context, stepId) {
         return await stepService
            .getStep(stepId)
            .then((response) => {
               return response;
            })
            .catch((error) => {
               throw error;
            });
      },
      async updateStep(context, { step }) {
         context.commit('setIsSaving', true);
         return await stepService
            .updateStep(step)
            .then((response) => {
               context.commit('setIsSaving', false);
               return response;
            })
            .catch((error) => {
               context.commit('setIsSaving', false);

               throw error;
            });
      },
      async deleteStep(context, { stepId, stepSequence }) {
         context.commit('setIsSaving', true);
         context.commit('deleteStep', {
            stepId: stepId,
            stepSequence: stepSequence,
         });
         return await stepService.deleteStep(stepId).then((response) => {
            context.commit('setIsSaving', false);
         });
      },
      async duplicateStep(context, { stepId }) {
         context.commit('setIsSaving', true);

         return await stepService.duplicateStep(stepId).then((response) => {
            context.commit('addStepToJourney', response);
            context.commit('setIsSaving', false);

            return response;
         });
      },
      async patchStep(context, { stepId, path, value }) {
         context.commit('setIsSaving', true);
         var body = [
            {
               op: 'replace',
               path: path,
               value: value,
            },
         ];
         return await stepService
            .patchStep(stepId, body)
            .then((response) => {
               context.commit('setIsSaving', false);
               return response;
            })
            .catch((error) => {
               context.commit('setIsSaving', false);

               throw error;
            });
      },
      async resetStep(context, { step }) {
         context.commit('setIsSaving', true);
         return await stepService.resetStep(step.id).then((response) => {
            context.commit('setIsSaving', false);
            if (!step.is_master_template) {
               context.commit('updateStepToJourney', { ...step, ...response });
               context.commit('setStep', { ...step, ...response });
            }
         });
      },
      //#endregion Step

      //#region StepRequirement
      async getStepRequirementsOptions(context, { stepId, cancelToken }) {
         return await stepRequirementService.getStepRequirementsOptions(stepId, cancelToken).then((response) => {
            return response;
         });
      },
      async getStepRequirements(context, { stepId, cancelToken }) {
         return await stepRequirementService.getStepRequirements(stepId, cancelToken).then((response) => {
            context.commit('setIsSaving', false);
            return response;
         });
      },
      async postRequirement(context, { stepId, sequence }) {
         context.commit('setIsSaving', true);
         return await stepRequirementService.postRequirement(stepId, sequence).then((response) => {
            return response;
         });
      },
      async deleteRequirement(context, requirementId) {
         context.commit('setIsSaving', true);
         return await stepRequirementService.deleteRequirement(requirementId).then((response) => {
            return response;
         });
      },
      async postRequirementTag(context, { requirementId, tagId, sequence }) {
         context.commit('setIsSaving', true);
         return await stepRequirementService.postRequirementTag(requirementId, tagId, sequence).then((response) => {
            return response;
         });
      },
      async patchRequirementTag(context, { requirementTagId, body }) {
         context.commit('setIsSaving', true);
         return await stepRequirementService.patchRequirementTag(requirementTagId, body).then((response) => {
            return response;
         });
      },
      async deleteRequirementTag(context, requirementTagId) {
         context.commit('setIsSaving', true);
         return await stepRequirementService.deleteRequirementTag(requirementTagId).then((response) => {
            return response;
         });
      },
      //#endregion StepRequirement

      //#region StepTemplate
      async getSearchStepTemplates(context, query) {
         return await stepTemplateService.searchStepTemplates(query).then((response) => {
            return response;
         });
      },

      async createTemplate(context, { stepId, isEditable }) {
         var body = {
            step_id: stepId,
            has_editable_children: isEditable,
         };
         return await stepTemplateService.createTemplate(body).then((response) => {
            context.commit('setIsSaving', false);
            return response;
         });
      },

      async patchTemplate(context, { templateId, path, value }) {
         return await stepTemplateService.patchTemplate(templateId, path, value).then((response) => {
            context.commit('updateStepToJourney', response);
            context.commit('setIsSaving', false);
            return response;
         });
      },
      //#endregion StepTemplate

      //#region Tag
      async createTag(context, { tagName }) {
         context.commit('setIsSaving', true);
         return await tagService.createTag({ name: tagName }).then((response) => {
            context.commit('setIsSaving', false);
            return response;
         });
      },
      async updateTag(context, { tagName, tagId }) {
         context.commit('setIsSaving', true);
         return await tagService.updateTag(tagId, { name: tagName }).then((response) => {
            context.commit('setIsSaving', false);
         });
      },
      async deleteTag(context, { tagId }) {
         context.commit('setIsSaving', true);
         return await tagService.deleteTag(tagId).then((response) => {
            context.commit('setIsSaving', false);
         });
      },
      //#endregion Tag

      //#region TenantJourney
      async postTenantJourney(context, { tenantId, journeyId }) {
         return await tenantJourneyService.postTenantJourney(tenantId, journeyId).then((response) => {
            return response;
         });
      },
      async deleteTenantJourney(context, tenantJourneyId) {
         return await tenantJourneyService.deleteTenantJourney(tenantJourneyId).then((response) => {
            return response;
         });
      },
      //#endregion TenantJourney

      //#region Text
      async createText(context, { textableId, textableType, contentType, language, text, cancelToken }) {
         context.commit('setIsSaving', true);
         var body = {
            textable_id: textableId,
            textable_type: textableType,
            type: contentType,
            content: {
               [language]: text,
            },
         };

         return await textService
            .postText(body, cancelToken)
            .then((response) => {
               context.commit('setIsSaving', false);
               return response;
            })
            .catch((error) => {
               context.commit('setIsSaving', false);
               throw error;
            });
      },
      async patchText(context, { textId, path, value, cancelToken }) {
         context.commit('setIsSaving', true);
         var body = [
            {
               op: 'replace',
               path: path,
               value: value,
            },
         ];
         return await textService
            .patchText(textId, body, cancelToken)
            .then((response) => {
               context.commit('setIsSaving', false);
               return response;
            })
            .catch((error) => {
               context.commit('setIsSaving', false);
               throw error;
            });
      },
      async deleteText(context, { id }) {
         context.commit('setIsSaving', true);
         await textService.delete(id);
         context.commit('setIsSaving', false);
      },
      //#endregion Text
   },
};

export default journeysStore;
